import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { useMemo } from "react";
import { colorPalette } from "../../styles/theme";
import { isSampleAccount, isOrgLevelAdmin } from "../../utils/auth";

/** 소속/사업 트리 — 마스터 관리자와 기관 관리자가 공유하는 렌더링 (organizations는 서버가 토큰 기준으로 이미 범위를 걸러 내려줌) */
const OrganizationTree = ({ heading, organizations, projectItemSx, projectTextProps, onProjectSelect }) => (
  <Paper sx={{ p: 2, backgroundColor: colorPalette.white }}>
    <Typography
      variant="caption"
      sx={{
        display: "block",
        fontWeight: 700,
        color: colorPalette.textSecondary,
        mb: 1,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
      }}
    >
      {heading}
    </Typography>
    <List dense disablePadding>
      {organizations.length === 0 ? (
        <Typography variant="caption" sx={{ color: colorPalette.textSecondary, px: 1 }}>
          등록된 소속이 없습니다.
        </Typography>
      ) : (
        organizations.map((organization) => (
          <Box key={organization.id}>
            <Box sx={{ borderRadius: 1, px: 1, py: 1 }}>
              <ListItemText
                primary={organization.name}
                primaryTypographyProps={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colorPalette.textPrimary,
                }}
              />
            </Box>
            <List dense disablePadding>
              {(organization.programs ?? []).map((program) => (
                <ListItemButton
                  key={program.id}
                  sx={projectItemSx(program)}
                  onClick={() =>
                    onProjectSelect?.({
                      code: program.code,
                      name: program.name,
                      organizationId: organization.id,
                      organizationName: organization.name,
                    })
                  }
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      backgroundColor: colorPalette.textSecondary,
                      mr: 1,
                      flexShrink: 0,
                    }}
                  />
                  <ListItemText primary={program.name} primaryTypographyProps={projectTextProps(program)} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        ))
      )}
    </List>
  </Paper>
);

/**
 * CODE별로 다르게 보이는 사이드바 네비게이션 트리
 *
 * 권한 정책:
 *  - level === 0: 마스터 관리자 - 모든 소속/사업 목록 + 문의 내역 표시
 *  - code 없음 & 마스터/샘플 아님 ("기관 관리자"): 서버가 토큰의 소속/level 기준으로 걸러 내려준
 *    organizations를 마스터와 동일한 트리로 표시 (문의 내역은 제외)
 *  - WS-YOUTH-2026: 해당 사업(2026 청년도전지원사업)의 메뉴만 표시
 *  - level 9 & code가 "ADMIN-TEST"로 시작, level === 10, 또는 소속이 "테스트": 샘플 화면 메뉴만 표시 (테스트 계정 전용)
 *  - 그 외: 관리 항목 없음 표시
 *
 * @param {object} me - 현재 로그인한 관리자 정보 (level/code 기반으로 메뉴 분기)
 * @param {string} currentView - 현재 선택된 뷰 ('participants' | 'surveyStats' | 'inquiry')
 * @param {Function} onViewChange - 뷰 전환 콜백 (viewName: string) => void
 * @param {Array} organizations - 전체 소속/사업 목록 (마스터 관리자 전용, /organizations API 응답)
 * @param {object|null} selectedProject - 현재 선택된 프로젝트(사업) { code, name, organizationName }
 * @param {Function} onProjectSelect - 프로젝트(사업) 선택 콜백 (project: object) => void
 */
const ProjectNav = ({
  me,
  currentView,
  onViewChange,
  organizations = [],
  selectedProject,
  onProjectSelect,
}) => {
  const projectLabelByCode = {
    "WS-YOUTH-2026": "2026 청년도전지원사업",
  };

  const projectLabel =
    projectLabelByCode[me?.code] ??
    ((me?.level === 9 && me?.code?.startsWith("ADMIN-TEST")) || isSampleAccount(me)
      ? "샘플 화면"
      : "소속 관리");

  // 소속/사업 목록을 name 오름차순으로 정렬 (원본 배열은 변경하지 않음)
  const sortedOrganizations = useMemo(() => {
    return [...organizations]
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "ko"))
      .map((organization) => ({
        ...organization,
        programs: [...(organization.programs ?? [])].sort((a, b) =>
          (a.name ?? "").localeCompare(b.name ?? "", "ko"),
        ),
      }));
  }, [organizations]);

  // 메뉴 아이템 스타일 헬퍼
  const menuItemSx = (viewName) => ({
    pl: 4,
    borderRadius: 1,
    backgroundColor:
      currentView === viewName ? colorPalette.activeBg : "transparent",
    "&:hover": { backgroundColor: colorPalette.activeBg },
  });

  const menuTextProps = (viewName) => ({
    fontSize: 13,
    color:
      currentView === viewName
        ? colorPalette.primary
        : colorPalette.textSecondary,
    fontWeight: currentView === viewName ? 600 : 400,
  });

  // 사이드바에서 선택된 프로젝트(사업)인지 여부 - currentView가 "sample"이고 code가 일치할 때만 활성 표시
  const isProjectSelected = (program) =>
    currentView === "sample" && selectedProject?.code === program.code;

  const projectItemSx = (program) => ({
    pl: 4,
    borderRadius: 1,
    backgroundColor: isProjectSelected(program) ? colorPalette.activeBg : "transparent",
    "&:hover": { backgroundColor: colorPalette.activeBg },
  });

  const projectTextProps = (program) => ({
    fontSize: 13,
    color: isProjectSelected(program) ? colorPalette.primary : colorPalette.textSecondary,
    fontWeight: isProjectSelected(program) ? 600 : 400,
  });

  // --- 마스터 관리자(level === 0): 전체 소속/사업 조회 (organizations API 데이터 그대로 출력) ---
  if (me?.level === 0) {
    return (
      <>
        <OrganizationTree
          heading="전체 관리"
          organizations={sortedOrganizations}
          projectItemSx={projectItemSx}
          projectTextProps={projectTextProps}
          onProjectSelect={onProjectSelect}
        />
        <Paper sx={{ p: 2, backgroundColor: colorPalette.white }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontWeight: 700,
              color: colorPalette.textSecondary,
              mb: 1,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            고객 센터
          </Typography>
          <List dense disablePadding>
            <ListItemButton
              sx={menuItemSx("inquiry")}
              onClick={() => onViewChange("inquiry")}
            >
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  backgroundColor: colorPalette.textSecondary,
                  mr: 1,
                  flexShrink: 0,
                }}
              />
              <ListItemText
                primary="문의 내역"
                primaryTypographyProps={menuTextProps("inquiry")}
              />
            </ListItemButton>
          </List>
        </Paper>
      </>
    );
  }

  // --- 기관 관리자: code 없이 소속 산하 여러 사업을 한 번에 조회 (마스터와 동일한 트리, 문의 내역 제외) ---
  if (isOrgLevelAdmin(me)) {
    return (
      <OrganizationTree
        heading={me?.organization || "소속 사업"}
        organizations={sortedOrganizations}
        projectItemSx={projectItemSx}
        projectTextProps={projectTextProps}
        onProjectSelect={onProjectSelect}
      />
    );
  }

  // --- 소속 관리자: SampleAdminView 기반 메뉴 ---
  if (me?.code || me?.level === 9 || isSampleAccount(me)) {
    return (
      <Paper sx={{ p: 2, backgroundColor: colorPalette.white }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontWeight: 700,
            color: colorPalette.textSecondary,
            mb: 1,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
          }}
        >
          {me?.organization || "소속"}
        </Typography>
        <List dense disablePadding>
          <ListItemButton
            sx={menuItemSx("sample")}
            onClick={() => onViewChange("sample")}
          >
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                backgroundColor: colorPalette.textSecondary,
                mr: 1,
                flexShrink: 0,
              }}
            />
            <ListItemText
              primary={projectLabel}
              primaryTypographyProps={menuTextProps("sample")}
            />
          </ListItemButton>
        </List>
      </Paper>
    );
  }

  // --- 그 외: 접근 가능한 사업 없음 ---
  return (
    <Paper sx={{ p: 2, backgroundColor: colorPalette.white }}>
      <Typography variant="body2" sx={{ color: colorPalette.textSecondary }}>
        접근 가능한 사업이 없습니다.
      </Typography>
    </Paper>
  );
};

export default ProjectNav;
